import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../CompanyContext';
import { locationsAPI, usersAPI } from '../../services/api';
import { Location, User, UserRole } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  BuildingOfficeIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const LocationsList: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedCompany) {
      fetchLocations();
      fetchManagers();
    }
  }, [selectedCompany]);

  const fetchLocations = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await locationsAPI.getAll();
      setLocations(data);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
      setError(err.response?.data?.message || 'Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    if (!selectedCompany) return;
    
    try {
      const data = await usersAPI.getAll();
      const branchManagers = data.filter(user => user.role === UserRole.BRANCH_MANAGER);
      setManagers(branchManagers);
    } catch (err: any) {
      console.error('Error fetching managers:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('common.confirmDelete'))) {
      return;
    }

    try {
      await locationsAPI.delete(id);
      setLocations(locations.filter(location => location.id !== id));
    } catch (err: any) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.message || 'Failed to delete location');
    }
  };

  const getManagerName = (managerId?: string) => {
    if (!managerId) return t('locations.noManager');
    const manager = managers.find(m => m.id === managerId);
    return manager ? `${manager.firstName} ${manager.lastName}` : t('locations.unknownManager');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-primary-600 mr-3" />
            {t('locations.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('locations.subtitle')}</p>
        </div>
        <Link
          to="/locations/new"
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>{t('locations.addLocation')}</span>
        </Link>
      </div>

      {/* Locations List */}
      {locations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('locations.noLocations')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('locations.noLocationsDescription')}
          </p>
          <Link
            to="/locations/new"
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{t('locations.addFirstLocation')}</span>
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {locations.map((location) => (
            <div
              key={location.id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                {/* Location Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${location.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <MapPinIcon className={`h-6 w-6 ${location.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {location.name}
                      </h3>
                      <div className="flex items-center mt-1">
                        {location.isActive ? (
                          <>
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                            <span className="text-sm text-green-600">{t('locations.status.active')}</span>
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-500">{t('locations.status.inactive')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {location.address && (
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      <span className="truncate">{location.address}</span>
                    </div>
                  )}
                  {location.phone && (
                    <div className="flex items-center">
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      <span>{location.phone}</span>
                    </div>
                  )}
                  {location.email && (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      <span className="truncate">{location.email}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 mr-2" />
                    <span>{getManagerName(location.managerId)}</span>
                  </div>
                </div>

                {/* Employee Count */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{t('locations.sections.employees')}</span>
                    <span className="text-lg font-bold text-primary-600">
                      {location.employees?.length || 0}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <Link
                    to={`/locations/${location.id}`}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span className="text-sm">{t('locations.actions.view')}</span>
                  </Link>
                  <Link
                    to={`/locations/${location.id}/edit`}
                    className="flex-1 bg-primary-100 text-primary-700 px-3 py-2 rounded-lg hover:bg-primary-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span className="text-sm">{t('locations.actions.edit')}</span>
                  </Link>
                  <button
                    onClick={() => handleDelete(location.id)}
                    className="flex-1 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="text-sm">{t('locations.actions.delete')}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationsList;
